import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Tooltip, Box } from '@mui/material';
import TableSkeleton from '../common/TableSkeleton';
import { Edit, Delete, Add, Visibility } from '@mui/icons-material';
import PageHeader from '../ui/PageHeader';
import { useAuth } from '../../services/authentication/authContext';
import { useTranslation } from 'react-i18next';
import { DataGrid } from '@mui/x-data-grid';
import { taskServices } from '../../services/taskServices';
import '../../css/DataTable.css';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const AssignedJob = () => {
  const [jobsData, setJobsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchJob, setSearchJob] = useState('');
  const [debouncedSearchJob, setDebouncedSearchJob] = useState(searchJob);
  const [columnFilter, setColumnFilter] = useState('');
  const [debouncedFilterJob, setDebouncedFilterJob] = useState(columnFilter);
  const [sortModel, setSortModel] = useState([{ field: 'updatedAt', sort: 'desc' }]);
  const { accessToken } = useAuth();
  const { t } = useTranslation();
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const pageSizeOptions = [5, 10, 15];
  const isSearchFirstRender = useRef(true);
  const isFilterFirstRender = useRef(true);
  const isSortFirstRender = useRef(true);
  const theme = useTheme();
  const isTabletOrBelow = useMediaQuery(theme.breakpoints.down('lg'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchData = async () => {
    setLoading(true);
    try {
      const clickedField = sortModel?.[0]?.field || 'updatedAt';
      const col = columns.find(c => c.field === clickedField);
      const sortField = col?.sortField || clickedField;
      const sortDir = sortModel[0]?.sort || "desc";

      const response = await taskServices.getCurrentJobsAssignedByUser(paginationModel.page, paginationModel.pageSize, debouncedSearchJob, debouncedFilterJob, true, sortField, sortDir);
      setJobsData(response.content);
      setRowCount(response.totalElements);

    } catch (error) {
      if (error.response && error.response.status === 404) {
        setJobsData([]);
        setRowCount(0);
      } else {
        console.error("Error fetching data", error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken) {
      fetchData(paginationModel.page, paginationModel.pageSize, debouncedSearchJob, debouncedFilterJob);
    }
  }, [accessToken, paginationModel, debouncedSearchJob, debouncedFilterJob, sortModel]);

  const handlePaginationModelChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);
  };

  useEffect(() => {
    if (isSearchFirstRender.current) {
      isSearchFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      const value = searchJob.trim();

      if (value === "" || value.length > 2) {
        setDebouncedSearchJob(value);
        setPaginationModel(prev => (prev.page === 0 ? prev : { ...prev, page: 0 }));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchJob]);

  useEffect(() => {
    if (isFilterFirstRender.current) {
      isFilterFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      const value = columnFilter.trim();

      if (value === "" || value.length > 2) {
        setDebouncedFilterJob(value);
        setPaginationModel(prev => (prev.page === 0 ? prev : { ...prev, page: 0 }));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [columnFilter]);

  // Columns for the job table
  const columns = useMemo(() => [
    {
      field: 'name', headerName: "Job", sortField: 'name', flex: 1, minWidth: isMobile ? 150 : 0, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value || '-'}</span></Tooltip>
      )
    },
    {
      field: "clientName", headerName: "Client", sortField: 'client.name', flex: 1, minWidth: isMobile ? 150 : 0, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value || '-'}</span></Tooltip>
      )
    },
    {
      field: "assignedByFirstName", headerName: t('Assigned User'), sortField: 'assignedBy.firstName', flex: 1, minWidth: isMobile ? 150 : 0, renderCell: (params) => (
        <Tooltip title={`${params.row.assignedByFirstName || ''} ${params.row.assignedByLastName || ''}`.trim()}>
          <span>{`${params.row.assignedByFirstName || ''} ${params.row.assignedByLastName || ''}`.trim() || '-'}</span>
        </Tooltip>
      )
    },
    {
      field: 'status', headerName: "Status", sortField: 'status', flex: 1, minWidth: isMobile ? 120 : 0, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value || '-'}</span></Tooltip>
      )
    },
    {
      field: 'type', headerName: t('job.job_type'), sortField: 'type', flex: 1, minWidth: isMobile ? 120 : 0, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value || '-'}</span></Tooltip>
      )
    },
    {
      field: 'createDate', headerName: t('job.create_date'), sortField: 'createDate', flex: 1, minWidth: isMobile ? 120 : 0, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value || '-'}</span></Tooltip>
      )
    },
    {
      field: 'expectedEndDate', headerName: t('job.end_date'), sortField: 'expectedEndDate', flex: 1, minWidth: isMobile ? 120 : 0, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value || '-'}</span></Tooltip>
      )
    },
    {
      field: 'budgetHours', headerName: t('job.budget_hours'), sortField: 'budgetHours', flex: 1, minWidth: isMobile ? 100 : 0, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value || '-'}</span></Tooltip>
      )
    }
  ], [isMobile, t]);

  const filteredDataMemo = useMemo(() => {
    if (!jobsData) return [];

    const filter = (job, search) => {
      const lowerSearch = search?.trim().toLowerCase() || '';
      return (
        (job?.name?.toLowerCase().includes(lowerSearch)) ||
        (job?.clientName?.toLowerCase().includes(lowerSearch)) ||
        (job?.status?.toLowerCase().includes(lowerSearch)) ||
        (job?.type?.toLowerCase().includes(lowerSearch)) ||
        (job?.createDate?.toLowerCase().includes(lowerSearch)) ||
        (job?.expectedEndDate?.toLowerCase().includes(lowerSearch)) ||
        (job?.assignedByFirstName?.toLowerCase().includes(lowerSearch)) ||
        (job?.assignedByLastName?.toLowerCase().includes(lowerSearch)) ||
        `${job?.assignedByFirstName} ${job?.assignedByLastName}`.toLowerCase().includes(lowerSearch)
      );
    };

    // Apply both filters
    return jobsData
      .filter(job => filter(job, searchJob) && filter(job, columnFilter))
      .map(job => ({
        ...job,
        client: job?.client?.name,
        assignedUser: job?.assignedUser ? `${job?.assignedUser.firstName ?? ''} ${job?.assignedUser?.lastName ?? ''}`.trim() : '-',
        createDate: job.createDate.split(' ')[0],
        expectedEndDate: job.expectedEndDate.split(' ')[0]
      }));

  }, [jobsData, searchJob, columnFilter]);

  const assignedJobsColumnVisibilityModel = useMemo(() => {
    if (isMobile) {
      return {
        // createDate: false,
        // expectedEndDate: false,
        // budgetHours: false,
      };
    }

    if (isTabletOrBelow) {
      return {
        // createDate: false,
        // budgetHours: false,
        // expectedEndDate: false,
      };
    }

    return {};
  }, [isMobile, isTabletOrBelow]);

  return (
    <div className="data-table-container">
      <PageHeader title="Assigned Jobs" />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <div style={{ flex: 1 }}></div>
        {/* General Search Filter */}
        <div className="search-container">
          <input
            type="text"
            placeholder="Type to search..."
            value={searchJob}
            onChange={(e) => setSearchJob(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <div className="data-table">
        <div className="data-grid-container" style={{ height: '70vh' }}>
          {loading ? (
            <Box sx={{ height: '100%', overflow: 'hidden' }}>
              <TableSkeleton rows={8} columns={6} />
            </Box>
          ) : (
            <DataGrid
              loading={false}
              rows={filteredDataMemo}
              columns={columns}
              pagination
              paginationMode="server"
              filterMode="server"
              onFilterModelChange={(newFilterModel) => {
                const value = newFilterModel.items?.[0]?.value || '';
                setColumnFilter(value);
              }}
              sortingMode="server"
              onSortModelChange={(newModel) => {
                if (isSortFirstRender.current) {
                  isSortFirstRender.current = false;
                  if (!newModel?.length) return;
                }
                if (!newModel?.length && sortModel.length > 0) {
                  const currentField = sortModel[0].field;
                  const isColumnDefined = columns.some(c => c.field === currentField);

                  if (!isColumnDefined) {
                    return;
                  }
                }
                setSortModel(newModel);
                setPaginationModel(prev => ({ ...prev, page: 0 }));
              }}
              sortModel={sortModel}
              paginationModel={paginationModel}
              rowCount={rowCount}
              pageSizeOptions={pageSizeOptions}
              onPaginationModelChange={handlePaginationModelChange}
              disableSelectionOnClick
              columnVisibilityModel={assignedJobsColumnVisibilityModel}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignedJob;
